/**
 * PublicClientGuidePage.jsx
 * ==========================
 *
 * Page publique de documentation pour les clients.
 * - Accessible sans login : /docs/client
 * - Source : fragment HTML généré depuis le DOCX via mammoth (backend /api/docs/client/html)
 * - Captures écran intégrées via /api/uploads/docs/html_assets/client/*
 * - Layout 3 colonnes Akamai-style via <GuideRenderer />
 * - **CTA en haut** : choix entre Web Player interactif (slideshow) et téléchargement PDF/PPTX
 *
 * RBAC : aucune perm requise (lecture publique des manuels)
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Book, PlayCircle, Download, FileText, ArrowRight } from 'lucide-react';
import GuideRenderer from '../components/docs/GuideRenderer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingHero = () => (
  <section
    data-testid="onboarding-hero"
    className="bg-gradient-to-br from-[#064E3B] via-[#047857] to-[#064E3B] text-white"
  >
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
      <div className="grid md:grid-cols-[1fr_auto] gap-6 md:gap-10 items-center">
        <div>
          <div className="text-xs md:text-sm font-bold tracking-[0.2em] uppercase text-[#FCD34D] mb-3">
            Nouveau · Formation d'onboarding interactive
          </div>
          <h2
            className="text-3xl md:text-4xl font-extrabold leading-tight mb-3"
            style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}
          >
            Découvrez Baobab Connect en <span className="text-[#FCD34D]">15 slides</span>.
          </h2>
          <p className="text-[#A7F3D0] text-base md:text-lg max-w-2xl leading-relaxed">
            Une présentation guidée pour comprendre la plateforme en ≈ 8 minutes : du devis à la facture, en passant par
            le suivi GPS et les Golden Rules opérationnelles.
          </p>
        </div>
        <div className="flex flex-col gap-3 min-w-[260px]">
          <Link
            to="/docs/client/onboarding"
            data-testid="onboarding-launch-web-btn"
            className="group inline-flex items-center justify-between gap-3 px-6 py-4 bg-[#FCD34D] text-[#064E3B] rounded-xl font-bold text-base shadow-lg hover:bg-[#FBBF24] transition-all"
          >
            <span className="flex items-center gap-3">
              <PlayCircle className="w-6 h-6" />
              <span>Lancer le web player</span>
            </span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
          <div className="grid grid-cols-2 gap-2">
            <a
              href={`${BACKEND_URL}/api/uploads/docs/Formation_Client_Baobab_Connect.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="onboarding-download-pdf-btn"
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-sm font-semibold text-white transition-colors"
            >
              <Download className="w-4 h-4" />
              PDF
            </a>
            <a
              href={`${BACKEND_URL}/api/uploads/docs/Formation_Client_Baobab_Connect.pptx`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="onboarding-download-pptx-btn"
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-sm font-semibold text-white transition-colors"
            >
              <FileText className="w-4 h-4" />
              PPTX
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const PublicClientGuidePage = () => {
  return (
    <div data-testid="public-client-guide-page">
      <OnboardingHero />
      {/* Centre de formation par module — Nouveau parcours pédagogique */}
      <section className="bg-gradient-to-br from-indigo-50 via-white to-emerald-50 border-y border-indigo-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
          <div className="grid md:grid-cols-[1fr_auto] gap-5 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-2 text-[11px] font-bold tracking-[0.22em] uppercase text-indigo-600">
                <Book className="w-3.5 h-3.5" />
                <span>Nouveau · Centre de formation par module</span>
              </div>
              <h2
                className="text-2xl md:text-3xl font-extrabold leading-tight mb-2 text-slate-900"
                style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}
              >
                10 modules pour maîtriser <span className="text-emerald-600">l'application à 100 %</span>.
              </h2>
              <p className="text-slate-600 text-sm md:text-base max-w-2xl leading-relaxed">
                Parcours pédagogique complet : Mes opérations, Mes contrats, Mes finances, Suivi temps réel, Documents, Évaluations, Rapports. Chaque module ~15-25 min avec quiz interactifs et cas pratiques.
              </p>
            </div>
            <div className="md:min-w-[230px]">
              <Link
                to="/docs/client/training"
                data-testid="client-training-launch-cta"
                className="group inline-flex items-center justify-between gap-3 px-5 py-3 bg-emerald-600 text-white rounded-lg font-bold text-sm shadow-md hover:bg-emerald-700 transition-all w-full"
              >
                <span className="flex items-center gap-2">
                  <PlayCircle className="w-5 h-5" />
                  <span>Ouvrir le centre de formation</span>
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>
      <GuideRenderer
        title="Guide Client — Baobab Connect"
        accentColor="emerald"
        IconBrand={Book}
        breadcrumbs={[
          { label: 'Documentation', href: '/docs/client' },
          { label: 'Guide Client' },
        ]}
        htmlEndpoint={`${BACKEND_URL}/api/docs/client/html`}
        metaEndpoint={`${BACKEND_URL}/api/docs/client/meta`}
        downloadDocxUrl={`${BACKEND_URL}/api/uploads/docs/Guide_Client_Baobab_Connect.docx`}
        backUrl="/dashboard"
        backLabel="Retour à l'application"
        testIdPrefix="client-guide"
      />
    </div>
  );
};

export default PublicClientGuidePage;
